import { isDefined, isNotDefined } from '@sgme/fp';

import { selectDataByCurrencyPair } from '@/api/config/selectors.ts';
import { userApi } from '@/api/user/index.ts';
import type { AppState } from '@/store/store.ts';
import { createWeakAppSelector } from '@/store/storeMethods.ts';
import { getIsStreamingConnected } from '@/store/streaming/slice.ts';
import { computeAppliedMarginSpotDeltaRate } from '@/store/swap/selectors/formulas/appliedMarginSpotDeltaRate.ts';
import { computeCommercialMarginAmount } from '@/store/swap/selectors/formulas/commercialMarginAmount.ts';
import { computeFarDeltaRate } from '@/store/swap/selectors/formulas/farDeltaRate.ts';
import { computeFarPrice } from '@/store/swap/selectors/formulas/farPrice.ts';
import { computeNearPrice } from '@/store/swap/selectors/formulas/nearPrice.ts';
import { computeSpot } from '@/store/swap/selectors/formulas/spot.ts';
import { selectSwapTile } from '@/store/swap/selectors/tile.ts';
import { selectWorkspaceCanTrade } from '@/store/workspace/slice.ts';
import { convertBidAskRateToBidAskPoints, convertRateToPoints } from '@/utils/number-point.ts';

export const getRequestSwapRfsMutationCacheKey = (tileId: string) => `swap-rfs-request-${tileId}`;

export const selectSwapPrices = createWeakAppSelector(
  (state: AppState, tileId: string) => selectSwapTile(state, tileId),
  (state: AppState) => selectDataByCurrencyPair(state),

  // user level clearance for trading
  (state: AppState) => userApi.endpoints.currentUser.select()(state).data?.canTrade ?? false,

  // workspace level lock state
  (state: AppState) => selectWorkspaceCanTrade(state),

  // SignalR is up: without it no new price can reach the browser, and an execution would be fired
  // without ever getting its ReceiveDealReply back (executeSwapRfs.ts listens on SignalR)
  (state: AppState) => getIsStreamingConnected(state),

  (tile, dataByCurrencyPair, userCanTrade, workspaceCanTrade, isStreamingConnected) => {
    if (isNotDefined(tile) || isNotDefined(tile.properties.nearNotional) || isNotDefined(tile.properties.farNotional)) {
      return undefined;
    }

    const currencyPair = tile.properties.currencyPair;

    if (isNotDefined(currencyPair) || isNotDefined(dataByCurrencyPair.data)) {
      return undefined;
    }

    const currencyPairData = dataByCurrencyPair.data.dict[currencyPair];

    if (isNotDefined(currencyPairData)) {
      return undefined;
    }

    const currencyPairPrecision = currencyPairData.precision;

    const rfs = tile.properties.rfs;

    if (isNotDefined(rfs) || rfs.status !== 'STREAMED') {
      return undefined;
    }

    const commercialMarginDefaultSpotPoints = convertBidAskRateToBidAskPoints(
      rfs.quote.defaultSpotMargin,
      currencyPairPrecision,
    );

    const commercialMarginDefaultSwapPoints = convertBidAskRateToBidAskPoints(
      rfs.quote.defaultMargin,
      currencyPairPrecision,
    );

    // -----------------------------------------------------
    // === updatable
    // -----------------------------------------------------

    // PinNearPriceClientBid (Client Near price - pinned)
    const pinnedNearPrice = tile.properties.nearPrice;

    const pinnedFarPrice =
      !tile.isNonDeliverable && tile.properties.type === 'historical' ? tile.properties.farPrice : undefined;

    // SpotComMarginBid (PPS spot margin)
    const commercialMarginSpotPoints = tile.properties.commercialMarginSpotPoints;

    // SwapComMarginBid (PPS Swap)
    const commercialMarginSwapPoints = tile.properties.commercialMarginSwapPoints;

    // -----------------------------------------------------
    // === quote ?
    // -----------------------------------------------------

    // NearPointsClientBid (Client Near points)
    // quote call it points, but it is normalized
    const nearDeltaRate = {
      bid: rfs.quote.nearPoints.bid,
      ask: rfs.quote.nearPoints.ask,
    };

    // SpotPriceTraderBid (Trader/Sales Spot price)
    const traderSpotPrice = {
      bid: rfs.quote.spot.bid,
      ask: rfs.quote.spot.ask,
    };

    // NearPointsTraderBid (Trader/Sales Near points)
    const traderNearDeltaRate = {
      bid: rfs.quote.nearSalesPoints?.bid ?? 0,
      ask: rfs.quote.nearSalesPoints?.ask ?? 0,
    };

    // FarPointsTraderBid (Trader/Sales Far points)
    const traderFarDeltaRate = {
      bid: rfs.quote.farPoints.bid,
      ask: rfs.quote.farPoints.ask,
    };

    const notionalCurrencyIndex = tile.properties.nearNotionalCurrencyIndex ?? 1;

    const commercialMarginAmountCurrencyIndex = tile.properties.commercialMarginAmountCurrencyIndex ?? 2;

    const commercialMarginAmountCurrency =
      commercialMarginAmountCurrencyIndex === 1 ? tile.properties.currency1 : tile.properties.currency2;

    // -----------------------------------------------------
    // === computed
    // -----------------------------------------------------

    const spot = computeSpot({
      nearNotional: tile.properties.nearNotional,
      farNotional: tile.properties.farNotional,
      pinnedNearPrice,
      traderSpotPrice,
      nearDeltaRate,
      marginSpotPps: commercialMarginSpotPoints ?? commercialMarginDefaultSpotPoints,
      currencyPairPrecision,
    });

    const farDeltaRate = computeFarDeltaRate({
      traderFarDeltaRate,
      marginSwapPps: commercialMarginSwapPoints ?? commercialMarginDefaultSwapPoints,
      currencyPairPrecision,
    });

    const nearPrice = computeNearPrice({
      pinnedNearPrice,
      spot,
      nearDeltaRate,
    });

    const farPrice = computeFarPrice({
      pinnedFarPrice,
      spot,
      farDeltaRate,
    });

    const appliedMarginSpotDeltaRate = computeAppliedMarginSpotDeltaRate({
      pinnedNearPrice,
      userGivenMarginSpotPoints: commercialMarginSpotPoints ?? commercialMarginDefaultSpotPoints,
      nearNotional: tile.properties.nearNotional,
      farNotional: tile.properties.farNotional,
      traderSpotPrice,
      spot,
      currencyPairPrecision,
    });

    const commercialMarginAmount = computeCommercialMarginAmount({
      notionalCurrencyIndex,
      nearNotional: tile.properties.nearNotional,
      farNotional: tile.properties.farNotional,

      spot,
      nearPrice,
      farPrice,

      traderSpotPrice,
      traderNearDeltaRate,
      traderFarDeltaRate,

      appliedMarginSpotDeltaRate,

      // DFNear (DF2 MTP near)
      nearDiscountFactor: rfs.quote.nearDf2 ?? 1,
      // DFFar (DF2 MTP far)
      farDiscountFactor: rfs.quote.farDf2 ?? 1,

      commercialMarginAmountCurrencyIndex,
    });

    // xxxx (Client Points de swaps)
    // =(FarPriceClientBid-NearPriceClientBid)*10^4
    const rates = {
      bid: farPrice.bid - nearPrice.bid,
      ask: farPrice.ask - nearPrice.ask,
    };

    const treasuryDelta =
      !tile.isNonDeliverable && tile.properties.type === 'historical' && isDefined(rfs.quote.farMktPoints)
        ? {
            bid: rfs.quote.farPoints.bid - rfs.quote.farMktPoints.bid,
            ask: rfs.quote.farPoints.ask - rfs.quote.farMktPoints.ask,
          }
        : undefined;

    /**
     * RFS: streamState.status === 'PRICING' && streamState.quote.quoteType === 'Tradeable'
     */
    const isExecutable = rfs.quote.quoteType === 'Tradeable';

    const isReadonly = !(userCanTrade && workspaceCanTrade) || !isStreamingConnected;

    return {
      currencyPairPrecision,
      rfsPrice: rfs,

      isExecutable,
      isReadonly,

      rates: {
        bid: convertRateToPoints(rates.bid, currencyPairPrecision - 1),
        ask: convertRateToPoints(rates.ask, currencyPairPrecision - 1),
      },

      spot,

      nearPoints: {
        bid: convertRateToPoints(nearDeltaRate.bid, currencyPairPrecision - 1),
        ask: convertRateToPoints(nearDeltaRate.ask, currencyPairPrecision - 1),
      },
      pinnedNearPrice,
      nearPrice,
      appliedMarginSpotDeltaRate,

      farPoints: {
        bid: convertRateToPoints(farDeltaRate.bid, currencyPairPrecision - 1),
        ask: convertRateToPoints(farDeltaRate.ask, currencyPairPrecision - 1),
      },

      farPrice,
      // True when far price is user-pinned (historical/off-market swap).
      // Used in buildSwapExecutionPrices to skip the formula-based computation
      // and send the exact user-entered value, as FX does.
      isPinnedFarPrice: isDefined(pinnedFarPrice),

      farMktPoints: isDefined(rfs.quote.farMktPoints)
        ? {
            bid: convertRateToPoints(rfs.quote.farMktPoints.bid, currencyPairPrecision - 1),
            ask: convertRateToPoints(rfs.quote.farMktPoints.ask, currencyPairPrecision - 1),
          }
        : undefined,

      treasuryDelta: isDefined(treasuryDelta)
        ? {
            bid: convertRateToPoints(treasuryDelta.bid, currencyPairPrecision - 1),
            ask: convertRateToPoints(treasuryDelta.ask, currencyPairPrecision - 1),
          }
        : undefined,

      commercialMarginSwapPoints,
      commercialMarginDefaultSwapPoints,
      commercialMarginAmount,
      commercialMarginAmountCurrencyIndex,
      commercialMarginAmountCurrency,

      isSaleTier: rfs.quote.distributionStream === 'STREAMDEFAULT',
    };
  },
);
